import React from "react";
import { DownloadSimple, X, Share, PlusSquare } from "@phosphor-icons/react";
import usePwaInstall from "@/hooks/usePwaInstall";

// Persistent-but-dismissable install banner shown at the top of the worker
// portal. On Chrome/Android it fires the native install prompt; on iOS
// Safari it shows the "Share -> Add to Home Screen" walkthrough (Apple
// deliberately doesn't expose beforeinstallprompt).
export default function InstallAppBanner() {
  const { canShow, install, dismiss, ios, hasNativePrompt } = usePwaInstall();
  const [showIosSteps, setShowIosSteps] = React.useState(false);

  if (!canShow) return null;

  const handleInstall = async () => {
    if (hasNativePrompt) {
      await install();
    } else if (ios) {
      setShowIosSteps(true);
    }
  };

  return (
    <div
      className="border-b border-[#1F2937] bg-[#030712] px-4 py-3 text-white"
      data-testid="pwa-install-banner"
    >
      <div className="mx-auto flex max-w-3xl items-center gap-3">
        <div className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-[#0044FF] text-lg font-black">
          H
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-black leading-tight">Install HCOB Worker</div>
          <div className="mt-0.5 truncate text-[11px] text-[#9CA3AF]">
            One-tap access, offline shell, full-screen — like a real app.
          </div>
        </div>
        <button
          onClick={handleInstall}
          className="whitespace-nowrap rounded-full bg-white px-4 py-2 text-xs font-black text-[#030712] hover:bg-[#F3F4F6]"
          data-testid="pwa-install-btn"
        >
          <DownloadSimple size={13} className="mr-1 inline" />
          Install
        </button>
        <button
          onClick={dismiss}
          aria-label="Dismiss install banner"
          className="rounded-full p-1 text-[#9CA3AF] hover:bg-[#1F2937] hover:text-white"
          data-testid="pwa-install-dismiss"
        >
          <X size={14} />
        </button>
      </div>

      {showIosSteps && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 px-4 py-6"
          onClick={() => setShowIosSteps(false)}
          data-testid="pwa-ios-modal"
        >
          <div
            className="w-full max-w-md rounded-t-2xl bg-white p-5 text-[#030712] sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-black uppercase tracking-widest text-[#4B5563]">
                  Install on iPhone
                </div>
                <div className="mt-1 text-lg font-black leading-tight">
                  Add HCOB Worker to your home screen
                </div>
              </div>
              <button
                onClick={() => setShowIosSteps(false)}
                aria-label="Close"
                className="p-1 text-[#4B5563] hover:text-[#030712]"
              >
                <X size={18} />
              </button>
            </div>
            <ol className="mt-4 space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[#030712] text-[11px] font-black text-white">
                  1
                </span>
                <span>
                  Tap the <Share size={13} className="inline align-text-bottom" />{" "}
                  <b>Share</b> icon at the bottom of Safari.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[#030712] text-[11px] font-black text-white">
                  2
                </span>
                <span>
                  Scroll down and tap{" "}
                  <PlusSquare size={13} className="inline align-text-bottom" />{" "}
                  <b>Add to Home Screen</b>.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[#030712] text-[11px] font-black text-white">
                  3
                </span>
                <span>
                  Tap <b>Add</b>. HCOB Worker will land on your home screen like a
                  regular app — open it and log in once.
                </span>
              </li>
            </ol>
            <div className="mt-4 text-[11px] text-[#4B5563]">
              iOS keeps the browser out of Apple's install flow — this is the same
              method every iPhone-installable app uses.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
